import { createContext, useContext, useReducer } from 'react';
import type { ReactElement, ReactNode } from 'react';

import type { IUpcomingMenu } from 'models/UpcomingMenu';
import UpcomingMenu from 'models/UpcomingMenu';
import FulfillmentStatus from 'models/FulfillmentStatus';

type Dispatch = (action: Action) => void;

type Action =
  | { type: 'toggleEnabled'; isEnabled: boolean }
  | { type: 'toggleDetailsExpanded'; areDetailsExpanded: boolean }
  | { type: 'toggleGalleryExpanded'; isGalleryExpanded: boolean }
  | { type: 'reload'; data: IUpcomingMenu };

type Props = {
  children: ReactNode;
  value: UpcomingMenu;
};

type ContextValue = {
  upcomingMenu: UpcomingMenu;
  dispatch: Dispatch;
};

const UpcomingMenuContext = createContext<ContextValue | null>(null);

function upcomingMenuReducer(
  upcomingMenu: UpcomingMenu,
  action: Action,
): UpcomingMenu {
  switch (action.type) {
    case 'toggleEnabled': {
      return new UpcomingMenu({
        ...upcomingMenu,
        fulfillment_status: action.isEnabled
          ? FulfillmentStatus.PLANNED
          : FulfillmentStatus.SKIPPED,
        planned_or_confirmed: action.isEnabled,
      });
    }

    case 'toggleDetailsExpanded': {
      return new UpcomingMenu({
        ...upcomingMenu,
        areDetailsExpanded: action.areDetailsExpanded,
      });
    }

    case 'toggleGalleryExpanded': {
      return new UpcomingMenu({
        ...upcomingMenu,
        isGalleryExpanded: action.isGalleryExpanded,
      });
    }

    case 'reload': {
      return new UpcomingMenu(action.data);
    }

    default: {
      return upcomingMenu;
    }
  }
}

function UpcomingMenuProvider({ children, value }: Props): ReactElement {
  const [upcomingMenu, dispatch] = useReducer(
    upcomingMenuReducer,
    value,
  );

  return (
    <UpcomingMenuContext.Provider value={{ upcomingMenu, dispatch }}>
      {children}
    </UpcomingMenuContext.Provider>
  );
}

function useUpcomingMenu(): ContextValue {
  const context = useContext(UpcomingMenuContext);

  if (context === null) {
    throw new Error(
      'useUpcomingMenu must be used within a UpcomingMenuProvider',
    );
  }

  return context;
}

export { useUpcomingMenu, UpcomingMenuProvider };
